import { ComponentProps, JSX } from "solid-js";
import c from "class-c";

import UObject from "@repo/utils/UObject";
import Color from "@repo/utils-client/Color";
import createPropsProvider from "@repo/utils-solid/createPropsProvider";

import styles from "./Card.module.scss";

declare namespace Card {
  type Props = UObject.Override<
    ComponentProps<"div">,
    {
      class?: string;
      raised?: boolean;
      dugout?: boolean;
      outline?: Color.String | boolean;
      style?: JSX.CSSProperties;
      children: JSX.Element;
      marginCollapsible?: boolean;
    }
  >;
}

function Card(_props: Card.Props) {
  const {
    children,
    dugout,
    raised,
    outline,
    class: className,
    marginCollapsible,
    ...props
  } = Card.PropsProvider.useMerge(_props) satisfies D;

  return (
    <div
      {...props}
      class={c`
        ${c(styles)`
          card
          ${{ dugout, raised, marginCollapsible }}
        `}
        ${className}
      `}
    >
      {marginCollapsible && <div class={styles["top-offset"]} />}
      {children}
      {marginCollapsible && <div class={styles["bottom-offset"]} />}
      {outline && (
        <div
          class={styles.outline}
          style={{
            "box-shadow": `inset 0 0 0 1px ${outline === true ? "var(--border-moderate)" : outline}`,
          }}
        />
      )}
    </div>
  );
}

declare namespace Card.Padding {
  type Props = ComponentProps<"div"> & {
    top?: boolean;
    right?: boolean;
    bottom?: boolean;
    left?: boolean;
  };
}

Card.Padding = ({
  class: className,
  top,
  right,
  bottom,
  left,
  ...props
}: Card.Padding.Props) => {
  return (
    <div
      class={c(styles)`
        padding
        ${!(top || right || bottom || left) ? "all" : ""}
        ${{ top, right, bottom, left }}
      `.c(className)}
      {...props}
    />
  );
};

declare namespace Card.EscapePadding {
  type Props = Card.Padding.Props;
}

Card.EscapePadding = ({
  class: className,
  top,
  right,
  bottom,
  left,
  ...props
}: Card.EscapePadding.Props) => {
  return (
    <div
      class={c(styles)`
        escape-padding
        ${!(top || right || bottom || left) ? "all" : ""}
        ${{ top, right, bottom, left }}
      `.c(className)}
      {...props}
    />
  );
};

Card.PropsProvider = createPropsProvider<Card.Props>("Card");

export default Card;
